import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, X, Calendar, FileImage } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/i18n";

export default function ClaimPhotoGallery({ claim }) {
  const { t } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/documents/by-claim/${claim.claim_id}/photos`)
      .then((r) => setPhotos(r.data))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [claim.claim_id]);

  const activeIdx = active === null ? -1 : photos.findIndex((p) => p.doc_id === active.doc_id);
  const prev = () => activeIdx > 0 && setActive(photos[activeIdx - 1]);
  const next = () => activeIdx < photos.length - 1 && setActive(photos[activeIdx + 1]);

  useEffect(() => {
    if (active === null) return;
    const h = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [active, activeIdx]);

  if (loading) return null;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid="claim-photo-gallery">
      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div>
          <div className="font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileImage className="w-4 h-4 text-[#003DA5]" /> {t("gallery.title")}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("gallery.subtitle")} · {photos.length}</div>
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="p-10 text-center" data-testid="gallery-empty">
          <Camera className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("gallery.empty")}</p>
        </div>
      ) : (
        <>
          {/* Horizontal swipe strip */}
          <div className="p-4 overflow-x-auto">
            <div className="flex gap-3">
              {photos.map((p, i) => (
                <button
                  key={p.doc_id}
                  onClick={() => setActive(p)}
                  className="shrink-0 relative group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-[#003DA5] focus:outline-none"
                  style={{ width: 160, height: 120 }}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img
                    src={`${BACKEND_URL}/api/documents/${p.doc_id}/download`}
                    alt={p.original_filename}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                    <div className="text-[9px] text-white font-mono truncate">{new Date(p.created_at).toLocaleDateString("en-IN")}</div>
                    <div className="text-[10px] text-white font-semibold truncate">{p.category}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/95 flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setActive(null); }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
              aria-label="Close"
              data-testid="gallery-close"
            >
              <X className="w-5 h-5" />
            </button>
            {activeIdx > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                data-testid="gallery-prev"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {activeIdx < photos.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                data-testid="gallery-next"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
            <motion.img
              key={active.doc_id}
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              src={`${BACKEND_URL}/api/documents/${active.doc_id}/download`}
              alt={active.original_filename}
              className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-white/10 backdrop-blur rounded-full px-4 py-1.5 font-mono flex items-center gap-2">
              <Calendar className="w-3 h-3" /> {new Date(active.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              · {active.category} · {activeIdx + 1}/{photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
